import {
	ProjectPublicationEventName,
	PublishingSequenceStep,
	InfrastructureResourceType,
	PublicationStepStatus
} from '@elyx-code/definitions';
import { toSentenceCase } from '@elyx-code/project-logic-tree';

export function getStepNameFromDefinition(
	stepType: PublishingSequenceStep
): string {
	switch (stepType) {
		case PublishingSequenceStep.CodeCompilation:
			return 'Code generation';
		case PublishingSequenceStep.RelationalDatabaseDeployment:
			return 'Database creation';
		case PublishingSequenceStep.HttpApiDeployment:
			return 'HTTP gateway creation';
		case PublishingSequenceStep.InfrastructureValuesCodebaseInterpolation:
			return 'Link code with resources';
		case PublishingSequenceStep.BundleCodebase:
			return 'Bundle code';
		case PublishingSequenceStep.MigrateRelationalDatabase:
			return 'Database structure migration';
		case PublishingSequenceStep.BusinessLogicCodebaseDeployment:
			return 'Update servers with new code';
		case PublishingSequenceStep.CodebaseCleanup:
			return 'Code cleanup';
		case PublishingSequenceStep.HttpApiDeletion:
			return 'HTTP gateway deletion';
		case PublishingSequenceStep.RelationalDatabaseDeletion:
			return 'Database deletion';
		case PublishingSequenceStep.ExecutePublicationCompletedEvent:
			return 'Execute custom publication completed logic';
		case PublishingSequenceStep.AWSTenantAccountProvisioning:
			return 'Provision private cloud';
		case PublishingSequenceStep.CloudFileSystemBucketDeletion:
			return 'Cloud file system storage deletion';
		case PublishingSequenceStep.SystemApiTokenProvisioning:
			return 'API key generation';
		case PublishingSequenceStep.CloudFileSystemBucketDeployment:
			return 'Cloud file system storage creation';
		case PublishingSequenceStep.ContainerComputeDeletion:
			return 'Container compute deletion';
		case PublishingSequenceStep.ContainerComputeDeployment:
			return 'Container compute creation';
		case PublishingSequenceStep.SystemApiTokenRevoking:
			return 'API key revoking';
		case PublishingSequenceStep.RevertRelationalDatabaseMigration:
			return 'Database migration rollback';
		case PublishingSequenceStep.SharedDriveDeletion:
			return 'Shared drive deletion';
		case PublishingSequenceStep.SharedDriveDeployment:
			return 'Shared drive creation';
		default:
			return toSentenceCase(stepType);
	}
}

export function getEventNameFromDefinition(
	event: ProjectPublicationEventName
): string {
	switch (event) {
		case ProjectPublicationEventName.DeploymentCompleted:
			return 'Successfully published';
		case ProjectPublicationEventName.DeploymentStarted:
			return 'Publication started';
		case ProjectPublicationEventName.DeploymentFailed:
			return 'Publication failed';
		case ProjectPublicationEventName.BillingSubscriptionUpdateFailed:
			return 'Billing subscription update failed';
		case ProjectPublicationEventName.AllInfrastructureSuccessfullyProvisioned:
			return 'All infrastructure successfully provisioned';
		case ProjectPublicationEventName.RequestPayloadValidationFailed:
			return 'Request payload validation failed';
		case ProjectPublicationEventName.ProjectStateValidationFailedMissingName:
			return 'Project state validation failed: missing name';
		case ProjectPublicationEventName.ProjectStateValidationFailedMissingAnyFunctionality:
			return 'Project state validation failed: missing any functionality';
		case ProjectPublicationEventName.RelationalDatabaseDeploymentStarted:
			return 'Database creation started';
		case ProjectPublicationEventName.RelationalDatabaseDeploymentCompleted:
			return 'Database creation completed';
		case ProjectPublicationEventName.RelationalDatabaseDeploymentFailed:
			return 'Database creation failed';
		case ProjectPublicationEventName.CreatingNewRelationalDatabase:
			return 'Creating new database (up to 5 mins)';
		case ProjectPublicationEventName.WaitingForPreviousRelationalDatabaseDeployment:
			return 'Waiting for previous database creation';
		case ProjectPublicationEventName.RelationalDatabaseAlreadyExists:
			return 'Database already exists';
		case ProjectPublicationEventName.HttpApiDeploymentStarted:
			return 'HTTP gateway creation started';
		case ProjectPublicationEventName.HttpApiDeploymentCompleted:
			return 'HTTP gateway creation completed';
		case ProjectPublicationEventName.HttpApiDeploymentFailed:
			return 'HTTP gateway creation failed';
		case ProjectPublicationEventName.CreatingNewHttpApi:
			return 'Creating new HTTP gateway';
		case ProjectPublicationEventName.HttpApiAlreadyExists:
			return 'HTTP gateway already exists';
		case ProjectPublicationEventName.BusinessLogicCodebaseDeploymentStarted:
			return 'Update servers with new code started';
		case ProjectPublicationEventName.BusinessLogicCodebaseDeploymentCompleted:
			return 'Update servers with new code completed';
		case ProjectPublicationEventName.BusinessLogicCodebaseDeploymentFailed:
			return 'Update servers with new code failed';
		case ProjectPublicationEventName.BundlingCodebaseForDownload:
			return 'Bundling code for download';
		case ProjectPublicationEventName.UploadingCodebaseToCloudStorage:
			return 'Uploading code to cloud storage';
		case ProjectPublicationEventName.InfrastructureValuesCodebaseInterpolationStarted:
			return 'Linking code with resources started';
		case ProjectPublicationEventName.InfrastructureValuesCodebaseInterpolationCompleted:
			return 'Linking code with resources completed';
		case ProjectPublicationEventName.InfrastructureValuesCodebaseInterpolationFailed:
			return 'Linking code with resources failed';
		case ProjectPublicationEventName.CodeCompilationStarted:
			return 'Code generation started';
		case ProjectPublicationEventName.CodeCompilationCompleted:
			return 'Code generation completed';
		case ProjectPublicationEventName.CodeCompilationFailed:
			return 'Code generation failed';
		case ProjectPublicationEventName.BundleCodebaseStarted:
			return 'Code bundling started';
		case ProjectPublicationEventName.BundleCodebaseCompleted:
			return 'Code bundling completed';
		case ProjectPublicationEventName.BundleCodebaseFailed:
			return 'Code bundling failed';
		case ProjectPublicationEventName.InstallingCodebaseDependenciesStarted:
			return 'Installing code dependencies';
		case ProjectPublicationEventName.InstallingCodebaseDependenciesCompleted:
			return 'Installing code dependencies completed';
		case ProjectPublicationEventName.InstallingCodebaseDependenciesFailed:
			return 'Installing code dependencies failed';
		case ProjectPublicationEventName.MigrateRelationalDatabaseStarted:
			return 'Database structure migration started';
		case ProjectPublicationEventName.MigrateRelationalDatabaseCompleted:
			return 'Database structure migration completed';
		case ProjectPublicationEventName.MigrateRelationalDatabaseFailed:
			return 'Database structure migration failed';
		case ProjectPublicationEventName.ProjectAlreadyBeingPublished:
			return 'Project already being published. You need to wait for the previous publication to complete';
		case ProjectPublicationEventName.ProjectNotFound:
			return 'Project not found';
		case ProjectPublicationEventName.ProjectPublishRequestAcknowledged:
			return 'Project publish request acknowledged';
		case ProjectPublicationEventName.UploadingCodebaseToCloudStorageCompleted:
			return 'Uploading code to cloud storage completed';
		case ProjectPublicationEventName.UploadingCodebaseToCloudStorageFailed:
			return 'Uploading code to cloud storage failed';
		case ProjectPublicationEventName.HttpApiDeletionCompleted:
			return 'HTTP gateway deletion completed';
		case ProjectPublicationEventName.HttpApiDeletionFailed:
			return 'HTTP gateway deletion failed';
		case ProjectPublicationEventName.HttpApiDeletionStarted:
			return 'HTTP gateway deletion started';
		case ProjectPublicationEventName.PublicationStepFailedReasonUnknown:
			return 'Publication step failed: reason unknown';
		case ProjectPublicationEventName.WaitingForPreviousHttpApiDeletion:
			return 'Waiting for previous HTTP gateway deletion';
		case ProjectPublicationEventName.RelationalDatabaseDeletionStarted:
			return 'Database deletion started';
		case ProjectPublicationEventName.RelationalDatabaseDeletionCompleted:
			return 'Database deletion completed';
		case ProjectPublicationEventName.RelationalDatabaseDeletionFailed:
			return 'Database deletion failed';
		case ProjectPublicationEventName.WaitingForPreviousRelationalDatabaseDeletion:
			return 'Waiting for previous database deletion';
		case ProjectPublicationEventName.ExecutePublicationCompletedEventCompleted:
			return 'Execution completed';
		case ProjectPublicationEventName.ExecutePublicationCompletedEventFailed:
			return 'Execution failed';
		case ProjectPublicationEventName.ExecutePublicationCompletedEventStarted:
			return 'Execution started';
		default: {
			if (!!event) {
				return toSentenceCase(event);
			}
			return 'Unknown event';
		}
	}
}

export enum PUBLISHING_DISABLED_REASONS {
	NO_LOGIC = 'NO_LOGIC',
	NO_NAME = 'NO_NAME',
	NO_CHANGES = 'NO_CHANGES',
	PROJECT_PUBLICATION_ALREADY_ONGOING = 'PROJECT_PUBLICATION_ALREADY_ONGOING',
	LOGIC_ERRORS = 'LOGIC_ERRORS',
	MISSING_ROW_TRANSFORMERS = 'MISSING_ROW_TRANSFORMERS'
}

export function fromPublishingDisabledCodeToReadable(
	code: PUBLISHING_DISABLED_REASONS
): string {
	switch (code) {
		case PUBLISHING_DISABLED_REASONS.NO_LOGIC:
			return 'This project has no logic';
		case PUBLISHING_DISABLED_REASONS.NO_NAME:
			return 'This project has no name';
		case PUBLISHING_DISABLED_REASONS.NO_CHANGES:
			return 'This version has already been published';
		case PUBLISHING_DISABLED_REASONS.PROJECT_PUBLICATION_ALREADY_ONGOING:
			return 'This project is already being published';
		case PUBLISHING_DISABLED_REASONS.LOGIC_ERRORS:
			return 'This project has errors in the logic. Resolve them before publishing';
		case PUBLISHING_DISABLED_REASONS.MISSING_ROW_TRANSFORMERS:
			return 'Some persisted data structures changed and need row transformers before publishing';
		default:
			return 'Publishing is currently not available. Try again later';
	}
}

export function resolveResourceNameFromType(
	resourceType: InfrastructureResourceType
): string {
	switch (resourceType) {
		case InfrastructureResourceType.RelationalDatabase:
			return 'Relational databases';
		case InfrastructureResourceType.HttpEndpoint:
		case InfrastructureResourceType.HttpApi:
			return 'HTTP endpoints';
		case InfrastructureResourceType.ServerlessFunction:
		case InfrastructureResourceType.CloudHostedCodebase:
			return 'Server code functions';
		case InfrastructureResourceType.ContainerCompute:
			return 'Container compute';
		case InfrastructureResourceType.SharedDrive:
			return 'Shared drives';
		case InfrastructureResourceType.CloudFileSystemBucket:
			return 'Cloud file systems';
		default:
			return 'Resources';
	}
}

export function resolvePublicationStepStatusFromType(
	eventType: PublicationStepStatus
): string {
	switch (eventType) {
		case PublicationStepStatus.Running:
			return 'In progress';
		case PublicationStepStatus.Failed:
			return 'Failed';
		case PublicationStepStatus.Success:
			return 'Succeeded';
		case PublicationStepStatus.Skipped:
			return 'Skipped';
		default:
			return 'Queued';
	}
}
